import { escapeHtml, icon } from "../learning-ui";
import {
  availableCourses,
  courseById,
  courses,
  defaultCourseId,
  getStudioScenes,
  k01ArtifactById,
  k01NarrationById,
  k01Package,
  k01SceneById,
  k01SceneNumber,
  k01Scenes,
  type CourseId,
  type K01Scene,
} from "./k01-content";
import { buildArtifactRecord, getArtifactCompletion, getSceneRunSnapshot, isArtifactFieldComplete, isSceneReady, renderSceneExperience, renderSceneFeedback, seedArtifactFromPriorChoices } from "./k01-renderers";
import {
  emptyK01SceneProgress,
  loadFramlageProgress,
  resetCourseProgress,
  saveFramlageProgress,
  type FramlageProgress,
  type K01Progress,
  type K01SceneProgress,
} from "../../state/k01-progress";

const chainFallback = ["result", "data", "human", "model", "training", "inference"];
const driverIds = ["datorkraft", "datatillgång", "investeringar", "automatiseringsbehov", "arbetsmiljö", "säkerhet", "efterfrågan"];
const K01_MEDIA_MANIFEST = "/media/audio/framlage/k01/media-manifest.json";

interface K01TimedCue {
  startSeconds: number;
  endSeconds: number;
  text: string;
}

interface K01RuntimeMedia {
  sceneId: string;
  file: string;
  cues: K01TimedCue[];
}

export class K01App {
  private appProgress: FramlageProgress = loadFramlageProgress();
  private motionReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  private storageWarning = "";
  private resetArmed = false;
  private readonly mediaByScene = new Map<string, K01RuntimeMedia>();
  private readonly audioPositions = new Map<string, number>();

  constructor(private readonly root: HTMLElement) {}

  private get progress(): K01Progress {
    return this.appProgress.courses[this.currentCourseId()];
  }

  start(): void {
    document.body.classList.add("k01-active");
    document.documentElement.classList.toggle("k01-reduced-motion", this.motionReduced);
    this.root.addEventListener("click", (event) => void this.handleClick(event));
    this.root.addEventListener("change", (event) => this.handleChange(event));
    this.root.addEventListener("input", (event) => this.handleTextInput(event));
    window.addEventListener("hashchange", () => {
      this.openHashScene();
      this.render();
      if (this.appProgress.activeView === "scene") this.focusSceneTitle();
    });
    this.openHashScene();
    this.render();
    void this.loadMediaManifest();
  }

  private openHashScene(): void {
    const match = window.location.hash.match(/^#course=(general|el-etg)(?:&(scene)=(K01-S\d{2}-C\d{2})|&(view)=(data))?$/);
    if (match) {
      const courseId = match[1] as CourseId;
      this.appProgress.activeCourseId = courseId;
      if (match[2] === "scene" && match[3] && k01SceneById.has(match[3])) {
        this.appProgress.activeView = "scene";
        this.progress.currentSceneId = match[3];
        this.markCourseStarted(courseId);
        this.seedCourseContext(match[3]);
      } else if (match[4] === "view" && match[5] === "data") {
        this.appProgress.activeView = "data";
      } else {
        this.appProgress.activeView = "course";
      }
      return;
    }
    const legacyMatch = window.location.hash.match(/^#scene=(K01-S\d{2}-C\d{2})$/);
    if (legacyMatch && k01SceneById.has(legacyMatch[1])) {
      const courseId = this.appProgress.activeCourseId ?? defaultCourseId;
      this.appProgress.activeCourseId = courseId;
      this.appProgress.activeView = "scene";
      this.progress.currentSceneId = legacyMatch[1];
      this.markCourseStarted(courseId);
      this.seedCourseContext(legacyMatch[1]);
    }
  }

  private currentCourseId(): CourseId {
    return this.appProgress.activeCourseId ?? defaultCourseId;
  }

  private currentScene(): K01Scene {
    return k01SceneById.get(this.progress.currentSceneId) ?? k01Scenes[0];
  }

  private currentCourse() {
    return courseById.get(this.currentCourseId()) ?? availableCourses[0];
  }

  private audioPositionKey(sceneId: string): string {
    return `${this.currentCourseId()}:${sceneId}`;
  }

  private currentSceneProgress(): K01SceneProgress {
    const sceneId = this.currentScene().id;
    this.progress.sceneProgress[sceneId] ??= emptyK01SceneProgress();
    return this.progress.sceneProgress[sceneId];
  }

  private seedCourseContext(sceneId: string): void {
    this.progress.sceneProgress[sceneId] ??= emptyK01SceneProgress();
    const scene = k01SceneById.get(sceneId);
    const artifact = scene ? k01ArtifactById.get(scene.interaction.artifactId) : undefined;
    const sceneProgress = this.progress.sceneProgress[sceneId];
    const courseTrack = this.currentCourse().transferTrack;
    if (courseTrack && artifact?.fields.some((field) => field.id === "transferTrack") && sceneProgress.values["artifact:transferTrack"] !== courseTrack) {
      sceneProgress.values["artifact:transferTrack"] = courseTrack;
      delete sceneProgress.values["artifact:systemCaseId"];
    }
    if (scene) seedArtifactFromPriorChoices(scene, sceneProgress, this.progress);
  }

  private markCourseStarted(courseId: CourseId): void {
    if (!this.appProgress.startedCourseIds.includes(courseId)) this.appProgress.startedCourseIds.push(courseId);
  }

  private persist(): void {
    this.storageWarning = saveFramlageProgress(this.appProgress)
      ? ""
      : "Lokal lagring är blockerad. Du kan fortsätta nu, men återupptagning efter omladdning kan saknas.";
  }

  private async handleClick(event: MouseEvent): Promise<void> {
    const button = (event.target as HTMLElement).closest<HTMLElement>("[data-k01-action]");
    if (!button) return;
    const action = button.dataset.k01Action ?? "";
    const focusKey = button.dataset.key;
    const focusValue = button.dataset.value;

    switch (action) {
      case "go-library":
        this.appProgress.activeView = "library";
        this.appProgress.activeCourseId = null;
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        this.persist();
        this.render();
        this.scrollToTop();
        return;
      case "go-course":
        this.appProgress.activeView = "course";
        window.history.replaceState(null, "", `#course=${this.currentCourseId()}`);
        this.persist();
        this.render();
        this.scrollToTop();
        return;
      case "open-course": {
        const course = courseById.get(button.dataset.courseId ?? "");
        if (!course || course.status !== "available" || (course.id !== "general" && course.id !== "el-etg")) return;
        this.appProgress.activeCourseId = course.id;
        this.appProgress.activeView = "course";
        window.history.replaceState(null, "", `#course=${course.id}`);
        this.persist();
        this.render();
        this.scrollToTop();
        return;
      }
      case "open-data":
        this.appProgress.activeView = "data";
        window.history.replaceState(null, "", `#course=${this.currentCourseId()}&view=data`);
        this.persist();
        this.render();
        this.scrollToTop();
        return;
      case "open-scene":
        this.openScene(button.dataset.sceneId ?? k01Scenes[0].id);
        return;
      case "open-studio": {
        const firstOpen = getStudioScenes(button.dataset.studioId ?? "").find((scene) => !this.progress.completedSceneIds.includes(scene.id));
        this.openScene(firstOpen?.id ?? getStudioScenes(button.dataset.studioId ?? "")[0]?.id ?? k01Scenes[0].id);
        return;
      }
      case "previous-scene":
        this.moveScene(-1);
        return;
      case "next-scene":
        if (!isSceneReady(this.currentScene(), this.currentSceneProgress())) return;
        this.completeAndMove();
        return;
      case "toggle-motion":
        this.motionReduced = !this.motionReduced;
        document.documentElement.classList.toggle("k01-reduced-motion", this.motionReduced);
        this.render(focusKey);
        return;
      case "play-audio":
        await this.toggleAudio(button);
        return;
      case "set":
        {
          const sceneProgress = this.currentSceneProgress();
          const key = button.dataset.key ?? "";
          const selectedValue = button.dataset.value ?? "";
          sceneProgress.values[key] = selectedValue;
          if (this.currentScene().id === "K01-S01-C01" && key === "path" && selectedValue) {
            const visitedKey = `path-seen:${selectedValue}`;
            if (!sceneProgress.toggles.includes(visitedKey)) sceneProgress.toggles.push(visitedKey);
          }
          if (this.currentScene().id === "K01-S01-C05" && key === "interface") {
            delete sceneProgress.values.surfaceGuess;
            delete sceneProgress.values.surfaceEvidence;
          }
          if (key.startsWith("artifact:")) {
            sceneProgress.saved = false;
            if (key !== "artifact:teenChoice" && key !== "artifact:teenEdit") sceneProgress.numbers.artifactAdvanced = 1;
          }
          if (!(this.currentScene().id === "K01-S01-C05" && key === "surfaceEvidence")) sceneProgress.revealed = false;
        }
        this.persist();
        this.render(focusKey, focusValue);
        return;
      case "toggle": {
        const sceneProgress = this.currentSceneProgress();
        this.toggleSceneKey(sceneProgress, button.dataset.key ?? "");
        if ((button.dataset.key ?? "").startsWith("artifact:")) sceneProgress.saved = false;
        this.persist();
        this.render(focusKey);
        return;
      }
      case "toggle-limited": {
        const sceneProgress = this.currentSceneProgress();
        this.toggleLimited(sceneProgress, button.dataset.key ?? "", Number(button.dataset.limit ?? 2));
        this.persist();
        this.render(focusKey);
        return;
      }
      case "adjust": {
        const sceneProgress = this.currentSceneProgress();
        this.adjustNumber(sceneProgress, button.dataset.key ?? "", Number(button.dataset.delta ?? 0));
        this.persist();
        this.render(focusKey);
        return;
      }
      case "move": {
        const sceneProgress = this.currentSceneProgress();
        this.moveOrder(sceneProgress, button.dataset.order ?? "chain", Number(button.dataset.index), Number(button.dataset.delta));
        this.persist();
        this.render(focusKey, focusValue);
        return;
      }
      case "run": {
        const sceneProgress = this.currentSceneProgress();
        {
          const snapshot = getSceneRunSnapshot(this.currentScene().id, sceneProgress);
          if (snapshot) {
            const first = sceneProgress.values["run:first"];
            if (!first) sceneProgress.values["run:first"] = snapshot;
            else if (snapshot !== first) sceneProgress.values["run:after"] = snapshot;
            sceneProgress.values["run:last"] = snapshot;
          }
        }
        sceneProgress.runs += 1;
        this.persist();
        this.render(focusKey);
        this.focusConsequence();
        return;
      }
      case "toggle-artifact-advanced": {
        const sceneProgress = this.currentSceneProgress();
        sceneProgress.numbers.artifactAdvanced = sceneProgress.numbers.artifactAdvanced === 1 ? 0 : 1;
        this.persist();
        this.render(focusKey);
        return;
      }
      case "reveal": {
        const sceneProgress = this.currentSceneProgress();
        sceneProgress.revealed = !sceneProgress.revealed;
        this.persist();
        this.render(focusKey);
        return;
      }
      case "advance-phase": {
        const sceneProgress = this.currentSceneProgress();
        sceneProgress.numbers.phase = Math.min(3, (sceneProgress.numbers.phase ?? 0) + 1);
        this.persist();
        this.render(focusKey);
        return;
      }
      case "save-config": {
        const sceneProgress = this.currentSceneProgress();
        this.saveDriverConfiguration(sceneProgress, button.dataset.key ?? "configA");
        this.persist();
        this.render(focusKey);
        return;
      }
      case "save-artifact": {
        const sceneProgress = this.currentSceneProgress();
        this.saveArtifact(sceneProgress, button.dataset.artifactId ?? "");
        this.persist();
        this.render(focusKey);
        this.focusConsequence();
        return;
      }
      case "request-reset":
        this.resetArmed = true;
        this.render(focusKey);
        return;
      case "cancel-reset":
        this.resetArmed = false;
        this.render(focusKey);
        return;
      case "confirm-reset":
        this.appProgress = resetCourseProgress(this.appProgress, this.currentCourseId());
        this.resetArmed = false;
        window.history.replaceState(null, "", `#course=${this.currentCourseId()}`);
        this.persist();
        this.render();
        return;
    }
  }

  private handleChange(event: Event): void {
    const control = (event.target as HTMLElement).closest<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("[data-k01-input]");
    if (!control) return;
    const key = control.dataset.key ?? "";
    const sceneProgress = this.currentSceneProgress();
    if (control.dataset.k01Input === "number") {
      sceneProgress.numbers[key] = Number(control.value);
      if (this.currentScene().id === "K01-S04-C01" && key === "time" && !sceneProgress.toggles.includes("time:touched")) {
        sceneProgress.toggles.push("time:touched");
      }
    } else sceneProgress.values[key] = control.value.slice(0, 1600);
    if (key === "artifact:transferTrack") sceneProgress.values["artifact:systemCaseId"] = "";
    if (key.startsWith("artifact:")) {
      sceneProgress.saved = false;
      if (key !== "artifact:teenChoice" && key !== "artifact:teenEdit") sceneProgress.numbers.artifactAdvanced = 1;
    }
    sceneProgress.revealed = false;
    this.persist();
    this.render(key);
  }

  private handleTextInput(event: Event): void {
    const rangeControl = (event.target as HTMLElement).closest<HTMLInputElement>("input[type='range'][data-k01-input='number']");
    if (rangeControl) {
      const progress = this.currentSceneProgress();
      const key = rangeControl.dataset.key ?? "";
      const numericValue = Number(rangeControl.value);
      if (!key || !Number.isFinite(numericValue)) return;
      progress.numbers[key] = numericValue;
      if (this.currentScene().id === "K01-S04-C01" && key === "time" && !progress.toggles.includes("time:touched")) {
        progress.toggles.push("time:touched");
      }
      progress.revealed = false;
      this.persist();
      this.updateRangePreview(rangeControl, key, numericValue);
      this.updateContinueButton();
      return;
    }
    const control = (event.target as HTMLElement).closest<HTMLTextAreaElement>("textarea[data-k01-input='value']");
    if (!control) return;
    const progress = this.currentSceneProgress();
    const key = control.dataset.key ?? "";
    const wasSaved = progress.saved;
    progress.values[key] = control.value.slice(0, 1600);
    if (key.startsWith("artifact:")) progress.saved = false;
    this.persist();
    if (wasSaved) {
      this.render(key);
      return;
    }
    this.updateArtifactControls();
    this.updateContinueButton();
  }

  private updateRangePreview(control: HTMLInputElement, key: string, numericValue: number): void {
    const labels = key === "time"
      ? ["tidigt läge", "nuvarande läge", "nästa möjliga läge"]
      : key.startsWith("budget")
        ? ["0", "1", "2", "3"]
        : ["låg", "mellan", "hög"];
    const min = Number(control.min || 0);
    const label = labels[numericValue - min] ?? String(numericValue);
    const output = control.closest("label")?.querySelector<HTMLOutputElement>("output");
    if (output) output.textContent = label;

    if (key === "time") {
      const timeline = this.root.querySelector<HTMLElement>(".k01-driver-timeline");
      if (timeline) timeline.dataset.time = String(numericValue);
      const era = timeline?.querySelector<HTMLElement>(":scope > b");
      if (era) era.textContent = label;
      return;
    }

    if (key === "compute" || key === "data") {
      const marker = this.root.querySelector<HTMLElement>(".k01-two-axis__grid > b");
      marker?.style.setProperty(key === "compute" ? "--x" : "--y", String(numericValue));
      return;
    }

    const budgetIndex = ["budgetA", "budgetB", "budgetC"].indexOf(key);
    if (budgetIndex >= 0) {
      const board = this.root.querySelector<HTMLElement>(".k01-budget-board");
      const cards = [...(board?.querySelectorAll<HTMLElement>("article") ?? [])];
      const dots = cards[budgetIndex]?.querySelector<HTMLElement>("span");
      if (dots) dots.textContent = "●".repeat(numericValue) || "○";
      if (board?.dataset.ran === "true") {
        const values = ["budgetA", "budgetB", "budgetC"].map((item) => this.currentSceneProgress().numbers[item] ?? 1);
        const winner = values.indexOf(Math.max(...values));
        cards.forEach((card, index) => {
          card.classList.toggle("is-funded", index === winner);
          const status = card.querySelector<HTMLElement>("b");
          if (status) status.textContent = index === winner ? "utvecklas" : "väntar";
        });
      }
      return;
    }

    if (key.startsWith("driver:")) {
      const score = driverIds.reduce((sum, driver) => sum + (this.currentSceneProgress().numbers[`driver:${driver}`] ?? 1), 0);
      const outcome = this.driverOutcome(score);
      const consoleOutcome = this.root.querySelector<HTMLElement>(".k01-driver-console > header > span");
      if (consoleOutcome) consoleOutcome.textContent = outcome;
      const chain = this.root.querySelectorAll<HTMLElement>(".k01-driver-console .k01-causal-chain > span");
      if (chain[0]) chain[0].textContent = score <= 9 ? "begränsad möjlighet" : "resurser och behov möts";
      if (chain[1]) chain[1].textContent = outcome;
      if (chain[2]) chain[2].textContent = score >= 12 ? "större kontrollbehov" : "litet lokalt test";
      return;
    }

    if (key === "counterValue") {
      const openValue = this.root.querySelector<HTMLElement>(".k01-counterfactual .is-open small");
      if (openValue) openValue.textContent = label;
    }
  }

  private driverOutcome(score: number): string {
    if (score <= 5) return "Idén stannar som skiss";
    if (score <= 9) return "Ett litet lokalt försök är möjligt";
    if (score <= 12) return "Idén kan prototypas och testas";
    return "Stark utvecklingskraft – och större krav på kontroll";
  }

  private toggleSceneKey(progress: K01SceneProgress, key: string): void {
    if (this.currentScene().id === "K01-S03-C03" && key.startsWith("signal:")) {
      const wasSelected = progress.toggles.includes(key);
      progress.toggles = progress.toggles.filter((item) => !item.startsWith("signal:"));
      if (!wasSelected) progress.toggles.push(key);
      progress.revealed = false;
      return;
    }
    progress.toggles = progress.toggles.includes(key) ? progress.toggles.filter((item) => item !== key) : [...progress.toggles, key];
    progress.revealed = false;
  }

  private toggleLimited(progress: K01SceneProgress, key: string, limit: number): void {
    if (progress.toggles.includes(key)) {
      progress.toggles = progress.toggles.filter((item) => item !== key);
      return;
    }
    const prefix = key.split(":")[0] + ":";
    if (progress.toggles.filter((item) => item.startsWith(prefix)).length < limit) progress.toggles.push(key);
  }

  private adjustNumber(progress: K01SceneProgress, key: string, delta: number): void {
    const limits: Record<string, [number, number]> = { caseIndex: [0, 5], driverPage: [0, 3], artifactPage: [0, 7], artifactDriverPage: [0, 6] };
    const [min, max] = limits[key] ?? [-100, 100];
    progress.numbers[key] = Math.max(min, Math.min(max, (progress.numbers[key] ?? 0) + delta));
  }

  private moveOrder(progress: K01SceneProgress, orderKey: string, index: number, delta: number): void {
    const order = [...(progress.orders[orderKey] ?? chainFallback)];
    const destination = index + delta;
    if (!Number.isInteger(index) || destination < 0 || destination >= order.length) return;
    [order[index], order[destination]] = [order[destination], order[index]];
    progress.orders[orderKey] = order;
  }

  private saveDriverConfiguration(progress: K01SceneProgress, key: string): void {
    const values = Object.fromEntries(driverIds.map((driver) => [driver, progress.numbers[`driver:${driver}`] ?? 1]));
    const score = Object.values(values).reduce((sum, item) => sum + item, 0);
    progress.values[key] = JSON.stringify({ values, score });
  }

  private saveArtifact(progress: K01SceneProgress, artifactId: string): void {
    const artifact = k01ArtifactById.get(artifactId);
    if (!artifact) return;
    const record = buildArtifactRecord(artifact, progress);
    if (!record) return;
    this.progress.artifacts[artifactId] = record;
    progress.saved = true;
  }

  private openScene(sceneId: string): void {
    if (!k01SceneById.has(sceneId)) return;
    const courseId = this.currentCourseId();
    this.appProgress.activeCourseId = courseId;
    this.markCourseStarted(courseId);
    this.progress.currentSceneId = sceneId;
    this.appProgress.activeView = "scene";
    this.seedCourseContext(sceneId);
    window.history.replaceState(null, "", `#course=${courseId}&scene=${sceneId}`);
    this.persist();
    this.render();
    this.focusSceneTitle();
    window.scrollTo({ top: 0, behavior: this.motionReduced ? "auto" : "smooth" });
  }

  private moveScene(delta: number): void {
    const index = k01Scenes.findIndex((scene) => scene.id === this.currentScene().id);
    const next = k01Scenes[index + delta];
    if (next) this.openScene(next.id);
    else {
      this.appProgress.activeView = "course";
      window.history.replaceState(null, "", `#course=${this.currentCourseId()}`);
      this.persist();
      this.render();
    }
  }

  private completeAndMove(): void {
    const scene = this.currentScene();
    if (!this.progress.completedSceneIds.includes(scene.id)) this.progress.completedSceneIds.push(scene.id);
    this.persist();
    this.moveScene(1);
  }

  private render(focusKey?: string, focusValue?: string): void {
    this.captureAudioPosition();
    if (this.appProgress.activeView === "scene") this.root.innerHTML = this.renderScene();
    else if (this.appProgress.activeView === "data") this.root.innerHTML = this.renderData();
    else if (this.appProgress.activeView === "course") this.root.innerHTML = this.renderCourseHome();
    else this.root.innerHTML = this.renderLibrary();
    if (this.appProgress.activeView === "scene") this.wireAudio();
    this.restoreFocus(focusKey, focusValue);
  }

  private shell(content: string, options: { dark?: boolean; progress?: number; chip?: string } = {}): string {
    const courseSubtitle = this.appProgress.activeCourseId ? this.currentCourse().title : "Kursbibliotek";
    return `<div class="app-shell k01-shell ${options.dark ? "k01-shell--dark" : ""}">
      <header class="k01-topbar">
        <button class="k01-brand" type="button" data-k01-action="go-library" aria-label="Till kursbiblioteket"><span>F</span><b>Framläge<small>${escapeHtml(courseSubtitle)}</small></b></button>
        <div class="k01-top-actions"><button class="k01-motion-button" type="button" data-k01-action="toggle-motion" data-key="motion" aria-pressed="${this.motionReduced}">${this.motionReduced ? "Lugn vy" : "Rörelse på"}</button>${options.chip ? `<span>${escapeHtml(options.chip)}</span>` : ""}</div>
        ${options.progress !== undefined ? `<div class="k01-top-progress" aria-hidden="true"><i style="width:${options.progress}%"></i></div>` : ""}
      </header>${this.storageWarning ? `<div class="k01-storage-warning" role="status">${escapeHtml(this.storageWarning)}</div>` : ""}${content}</div>`;
  }

  private courseIcon(courseId: string): string {
    return icon(courseId === "general" ? "book" : courseId === "el-etg" ? "spark" : "shield");
  }

  private renderLibraryCourseCard(courseId: CourseId, compact = false): string {
    const course = courseById.get(courseId) ?? availableCourses[0];
    const courseProgress = this.appProgress.courses[courseId];
    const completed = courseProgress.completedSceneIds.length;
    const percent = Math.round((completed / k01Scenes.length) * 100);
    const started = this.appProgress.startedCourseIds.includes(courseId);
    const status = completed === k01Scenes.length ? "Klar" : started ? "Pågår" : "Tillgänglig";
    return `<button class="k01-course-card ${compact ? "is-compact" : ""} is-${escapeHtml(course.visualKey)}" type="button" data-k01-action="open-course" data-course-id="${course.id}" aria-label="${escapeHtml(course.title)}, ${status.toLowerCase()}">
      <span class="k01-course-icon">${this.courseIcon(course.id)}</span>
      <span class="k01-course-card-copy"><small>${escapeHtml(status)}</small><strong>${escapeHtml(course.title)}</strong><span>${escapeHtml(course.catalogDescription)}</span></span>
      ${started ? `<span class="k01-course-card-progress"><i style="width:${percent}%"></i><b>${completed}/26</b></span>` : `<span class="k01-course-card-arrow">${icon("arrow")}</span>`}
    </button>`;
  }

  private renderLibrary(): string {
    const startedCourses = availableCourses.filter((course) => this.appProgress.startedCourseIds.includes(course.id));
    const myCourses = startedCourses.map((course) => this.renderLibraryCourseCard(course.id)).join("");
    const myCoursesHeading = startedCourses.length === 1 ? "Fortsätt din kurs" : "Fortsätt dina kurser";
    const catalogCards = courses.map((course) => {
      if (course.status === "planned" || (course.id !== "general" && course.id !== "el-etg")) {
        return `<article class="k01-course-card is-planned is-${escapeHtml(course.visualKey)}" aria-label="${escapeHtml(course.title)}, planerad kurs">
          <span class="k01-course-icon">${this.courseIcon(course.id)}</span>
          <span class="k01-course-card-copy"><small>Planerad kurs</small><strong>${escapeHtml(course.title)}</strong><span>${escapeHtml(course.catalogDescription)}</span></span>
          <span class="k01-course-card-arrow">${icon("lock")}</span>
        </article>`;
      }
      return this.renderLibraryCourseCard(course.id, startedCourses.length > 0);
    }).join("");
    return this.shell(`
      <main class="k01-library">
        <section class="k01-library-hero">
          <div><p class="k01-kicker">${startedCourses.length ? "Välkommen tillbaka" : "Framläge"}</p><h1>${startedCourses.length ? "Fortsätt där du slutade." : "Välj din första kurs."}</h1><p>Varje kurs har en egen kursstart, egna exempel och en helt separat progression.</p></div>
          <div class="k01-hero-machine" aria-hidden="true"><div class="is-human"><b>Människa</b><i></i><i></i><i></i></div><div class="is-rule"><b>Regel</b><i></i><i></i><i></i></div><div class="is-model"><b>Modell</b><i></i><i></i><i></i></div><span>?</span></div>
        </section>
        ${startedCourses.length ? `<section class="k01-my-courses" aria-labelledby="k01-my-courses-title"><header><small>MINA KURSER</small><h2 id="k01-my-courses-title">${myCoursesHeading}</h2></header><div class="k01-course-grid">${myCourses}</div></section>` : ""}
        <section class="k01-course-catalog" aria-labelledby="k01-course-catalog-title"><header><small>KURSBIBLIOTEK</small><h2 id="k01-course-catalog-title">${startedCourses.length ? "Alla kurser" : "Vad vill du lära dig?"}</h2><p>Öppna en kurs för att se innehållet. Kursen räknas som påbörjad först när du startar en del.</p></header><div class="k01-course-grid">${catalogCards}</div>
          <details class="k01-storage-disclosure"><summary>Inga spårningscookies · separat lokal kursprogression</summary><p>Påbörjade kurser, progression och egna svar sparas bara i den här webbläsaren. Varje kurs har en egen lokal progression. Appfiler och ljud kan sparas för offlineanvändning. Uppgifterna skickas inte till oss eller tredje part.</p></details>
        </section>
      </main>
    `, { dark: true, chip: "Kurser" });
  }

  private renderCourseHome(): string {
    const completed = this.progress.completedSceneIds.length;
    const current = this.currentScene();
    const course = this.currentCourse();
    const percent = Math.round((completed / k01Scenes.length) * 100);
    const allComplete = completed === k01Scenes.length;
    const studioCards = k01Package.studios.map((studio, index) => {
      const studioScenes = getStudioScenes(studio.id);
      const studioCompleted = studioScenes.filter((scene) => this.progress.completedSceneIds.includes(scene.id)).length;
      const state = studioCompleted === studioScenes.length ? "klar" : studioCompleted > 0 ? "pågår" : "inte startad";
      return `<button class="k01-studio-card" type="button" data-k01-action="open-studio" data-studio-id="${studio.id}" data-state="${state}"><span>${studioCompleted === studioScenes.length ? icon("check") : index + 1}</span><div><small>Studio ${index + 1} · ${studioCompleted}/${studioScenes.length}</small><strong>${escapeHtml(studio.title)}</strong><p>${escapeHtml(studio.learningGoal)}</p></div><i>${icon("arrow")}</i></button>`;
    }).join("");
    return this.shell(`
      <main class="k01-home">
        <section class="k01-hero">
          <div class="k01-hero-copy"><button class="k01-course-back" type="button" data-k01-action="go-library">${icon("back")} Alla kurser</button><p class="k01-kicker">AI nivå 1 · K1</p><h1>${escapeHtml(course.title)}</h1><p>${escapeHtml(course.description)}</p><div class="k01-hero-actions"><button class="k01-primary" type="button" data-k01-action="${allComplete ? "open-data" : "open-scene"}"${allComplete ? "" : ` data-scene-id="${escapeHtml(completed ? current.id : k01Scenes[0].id)}"`}>${icon(allComplete ? "check" : "play")}<span>${allComplete ? "K1 är klar – se dina byggen" : completed ? "Fortsätt kursen" : "Starta kursen"}</span></button>${allComplete ? "" : `<button class="k01-secondary" type="button" data-k01-action="open-data">Min lokala data</button>`}</div><small>Ingen inloggning · ingen levande AI · den här kursens progression stannar på enheten</small></div>
          <div class="k01-hero-machine" aria-hidden="true"><div class="is-human"><b>Människa</b><i></i><i></i><i></i></div><div class="is-rule"><b>Regel</b><i></i><i></i><i></i></div><div class="is-model"><b>Modell</b><i></i><i></i><i></i></div><span>?</span></div>
        </section>
        <section class="k01-home-progress"><div class="k01-progress-summary"><div><small>Din progression i ${escapeHtml(course.shortLabel)}</small><strong>${completed} av 26 scener</strong></div><div class="k01-progress-ring" style="--progress:${percent * 3.6}deg"><span>${percent}%</span></div></div><details class="k01-storage-disclosure"><summary>Inga spårningscookies · lokal kurslagring</summary><p>Progression och egna svar för ${escapeHtml(course.title)} sparas separat från andra kurser och bara i den här webbläsaren. Appfiler och ljud kan sparas för offlineanvändning. När lärgrindarna ändras kan äldre scenprogress och byggen nollställas säkert. Kursens data kan raderas under Min lokala data.</p></details></section>
        <section class="k01-studio-list" aria-labelledby="k01-studio-list-title"><header><p class="k01-kicker">K1 · AI-kickstart</p><h2 id="k01-studio-list-title">Fyra studior i den här kursdelen</h2><p>Öppna en studio eller fortsätt från huvudknappen ovan. Dina resultat hör bara till den här kursen.</p></header>${studioCards}</section>
      </main>
    `, { dark: true, chip: "K1" });
  }

  private renderScene(): string {
    const scene = this.currentScene();
    const state = this.currentSceneProgress();
    const narration = k01NarrationById.get(scene.narrationId);
    const studio = k01Package.studios.find((item) => item.id === scene.studioId) ?? k01Package.studios[0];
    const studioScenes = getStudioScenes(scene.studioId);
    const studioSceneIndex = studioScenes.findIndex((item) => item.id === scene.id);
    const globalIndex = k01SceneNumber(scene.id) - 1;
    const previous = k01Scenes[globalIndex - 1];
    const isLast = globalIndex === k01Scenes.length - 1;
    const ready = isSceneReady(scene, state);
    const savedAudioPosition = this.audioPositions.get(this.audioPositionKey(scene.id)) ?? 0;
    const timedCaption = this.mediaByScene.get(scene.id)?.cues.find((cue) => savedAudioPosition >= cue.startSeconds && savedAudioPosition < cue.endSeconds)?.text;
    const fallbackCueIndex = narration ? Math.min(narration.captionCues.length - 1, Math.floor(Math.max(0, Math.min(1, savedAudioPosition / narration.targetSeconds)) * narration.captionCues.length)) : 0;
    const caption = timedCaption ?? narration?.captionCues[fallbackCueIndex] ?? narration?.spokenLine ?? "";
    return this.shell(`
      <main class="k01-scene-screen">
        <nav class="k01-scene-path" aria-label="Scener i ${escapeHtml(studio.title)}"><button type="button" data-k01-action="go-course">${icon("back")}<span>Kursstart</span></button><div><small>Studio ${k01Package.studios.findIndex((item) => item.id === studio.id) + 1}/4 · scen ${studioSceneIndex + 1}/${studioScenes.length}</small><strong>${escapeHtml(studio.title)}</strong></div><ol>${studioScenes.map((item, index) => `<li class="${this.progress.completedSceneIds.includes(item.id) ? "is-done" : item.id === scene.id ? "is-current" : ""}" aria-current="${item.id === scene.id ? "step" : "false"}"><span>${index + 1}</span></li>`).join("")}</ol></nav>
        <header class="k01-scene-heading"><p class="k01-kicker">Studio ${k01Package.studios.findIndex((item) => item.id === studio.id) + 1} · del ${studioSceneIndex + 1}</p><h1 tabindex="-1" data-k01-scene-title>${escapeHtml(scene.title)}</h1><p>${escapeHtml(scene.learnerPromise)}</p></header>
        ${narration ? narration.audioRevisionStatus === "regeneration-required" ? `<section class="k01-narrator k01-narrator--pending" aria-label="Berättarröst väntar på ny version"><div class="k01-caption"><small>Ny röstversion förbereds</small><strong>${escapeHtml(narration.spokenLine)}</strong></div><details open><summary>Läs som text</summary><p>${escapeHtml(narration.transcript)}</p></details></section>` : `<section class="k01-narrator" data-k01-narrator data-scene-id="${scene.id}" data-resume-position="${savedAudioPosition.toFixed(3)}"><audio preload="metadata" src="${escapeHtml(narration.audioFile)}" aria-label="Berättarröst för ${escapeHtml(scene.title)}" data-k01-audio></audio><button type="button" data-k01-action="play-audio" class="k01-listen-button">${icon("play")}<span>Lyssna</span></button><div class="k01-caption"><small>Berättarröst</small><strong data-k01-caption>${escapeHtml(caption)}</strong><div><i data-k01-audio-progress></i></div></div><details><summary>Läs som text</summary><p>${escapeHtml(narration.transcript)}</p></details></section>` : ""}
        <section class="k01-stage" data-motion-type="${escapeHtml(scene.motion.type)}"><div class="k01-stage-label"><span>GÖR SÅ HÄR</span><p>${escapeHtml(scene.actionLabel)}</p><details><summary>Visa ett tips</summary><p>${escapeHtml(scene.interaction.instruction)}</p></details></div>${renderSceneExperience(scene, state, this.progress)}</section>
        ${renderSceneFeedback(scene, state)}
      </main>
      <nav class="k01-bottom-nav" aria-label="Scennavigation"><button type="button" data-k01-action="previous-scene" ${!previous ? "disabled" : ""}>${icon("back")}<span>Tillbaka</span></button><div><small>${globalIndex + 1}/26</small><span id="k01-gate-message" data-k01-gate-message>${ready ? "Klart – du kan fortsätta" : "Prova klart först"}</span></div><button class="is-primary" type="button" data-k01-action="next-scene" aria-describedby="k01-gate-message" ${!ready ? "disabled" : ""}><span>${isLast ? "K1 klar" : studioSceneIndex === studioScenes.length - 1 ? "Nästa studio" : "Fortsätt"}</span>${icon("arrow")}</button></nav>
    `, { progress: ((globalIndex + 1) / k01Scenes.length) * 100, chip: "K1" });
  }

  private renderData(): string {
    const course = this.currentCourse();
    const artifactCards = k01Package.studios.map((studio) => {
      const artifact = k01ArtifactById.get(studio.artifactId);
      const saved = this.progress.artifacts[studio.artifactId];
      return `<article class="k01-data-card"><div>${icon(saved ? "check" : "kit")}</div><section><small>${saved ? "Sparat lokalt" : "Inte byggt ännu"}</small><strong>${escapeHtml(artifact?.title ?? studio.artifactId)}</strong><p>${saved ? `${Object.values(saved).filter(Boolean).length} delar finns på den här enheten.` : "Inget elevinnehåll finns ännu."}</p></section></article>`;
    }).join("");
    return this.shell(`<main class="k01-data-screen"><button class="k01-back-link" type="button" data-k01-action="go-course">${icon("back")} Till kursstarten</button><p class="k01-kicker">Samma insyn för elev och vuxen</p><h1>Lokal data för ${escapeHtml(course.title)}</h1><p>Här finns bara den här kursens valda scen, slutförda scener och de fyra byggen du själv sparar. Andra kurser har separat progression. Ingen tid, plats, identitet, klickhistorik eller extern aktivitet registreras.</p><section class="k01-data-summary"><strong>${escapeHtml(course.shortLabel)}</strong><span>kurs</span><strong>${this.progress.completedSceneIds.length}/26</strong><span>slutförda scener</span><strong>${Object.keys(this.progress.artifacts).length}/4</strong><span>sparade byggen</span></section><section class="k01-data-list">${artifactCards}</section><aside class="k01-data-boundary">${icon("shield")}<p><strong>Stannar på enheten</strong>Ingen backend, inget konto, ingen tredjepartsspårning och ingen dold föräldravisning.</p></aside><section class="k01-reset">${this.resetArmed ? `<p>Detta tar bara bort progressionen och de fyra lokala byggena för ${escapeHtml(course.title)}. Övriga kurser påverkas inte.</p><div><button type="button" data-k01-action="cancel-reset">Avbryt</button><button class="is-danger" type="button" data-k01-action="confirm-reset">Radera kursdata</button></div>` : `<button type="button" data-k01-action="request-reset" data-key="reset">Rensa data för den här kursen</button>`}</section></main>`, { chip: "Lokal data" });
  }

  private async toggleAudio(button: HTMLElement): Promise<void> {
    const narrator = button.closest<HTMLElement>("[data-k01-narrator]");
    const audio = narrator?.querySelector<HTMLAudioElement>("[data-k01-audio]");
    if (!narrator || !audio) return;
    if (!audio.paused) {
      audio.pause();
      return;
    }
    this.root.querySelectorAll<HTMLAudioElement>("[data-k01-audio]").forEach((other) => { if (other !== audio) other.pause(); });
    if (audio.ended) audio.currentTime = 0;
    const savedPosition = Math.max(
      this.audioPositions.get(this.audioPositionKey(narrator.dataset.sceneId ?? "")) ?? 0,
      Number(narrator.dataset.resumePosition ?? 0) || 0,
    );
    if (audio.currentTime < 0.1 && savedPosition > 0) {
      const seekToSavedPosition = (): void => {
        if (Number.isFinite(audio.duration) && savedPosition < audio.duration - 0.15) audio.currentTime = savedPosition;
      };
      if (audio.readyState >= 1) seekToSavedPosition();
      else audio.addEventListener("loadedmetadata", seekToSavedPosition, { once: true });
    }
    try {
      await audio.play();
      if (savedPosition > 0 && audio.currentTime < savedPosition - 0.2) {
        const seekAfterPlaybackStarts = (): void => {
          if (Number.isFinite(audio.duration) && savedPosition < audio.duration - 0.15) audio.currentTime = savedPosition;
        };
        if (audio.readyState >= 1) seekAfterPlaybackStarts();
        else audio.addEventListener("loadedmetadata", seekAfterPlaybackStarts, { once: true });
      }
    } catch {
      const caption = narrator.querySelector<HTMLElement>("[data-k01-caption]");
      if (caption) caption.textContent = "Ljudet kunde inte starta. Öppna Läs som text eller försök igen.";
    }
  }

  private wireAudio(): void {
    const narrator = this.root.querySelector<HTMLElement>("[data-k01-narrator]");
    const audio = narrator?.querySelector<HTMLAudioElement>("[data-k01-audio]");
    const button = narrator?.querySelector<HTMLButtonElement>("[data-k01-action='play-audio']");
    const caption = narrator?.querySelector<HTMLElement>("[data-k01-caption]");
    const bar = narrator?.querySelector<HTMLElement>("[data-k01-audio-progress]");
    const segment = k01NarrationById.get(this.currentScene().narrationId);
    if (!narrator || !audio || !button || !caption || !bar || !segment) return;
    const update = (): void => {
      const duration = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : segment.targetSeconds;
      const ratio = Math.max(0, Math.min(1, audio.currentTime / duration));
      if (audio.currentTime > 0 && !audio.ended) this.audioPositions.set(this.audioPositionKey(segment.sceneId), audio.currentTime);
      bar.style.width = `${ratio * 100}%`;
      const timedCues = this.mediaByScene.get(segment.sceneId)?.cues ?? [];
      const timedCue = timedCues.find((cue) => audio.currentTime >= cue.startSeconds && audio.currentTime < cue.endSeconds);
      const cueIndex = Math.min(segment.captionCues.length - 1, Math.floor(ratio * segment.captionCues.length));
      caption.textContent = timedCue?.text ?? segment.captionCues[cueIndex] ?? segment.spokenLine;
      narrator.dataset.phase = String(cueIndex + 1);
    };
    audio.addEventListener("timeupdate", update);
    audio.addEventListener("play", () => { button.innerHTML = `${icon("pause")}<span>Pausa</span>`; narrator.classList.add("is-playing"); });
    audio.addEventListener("pause", () => { button.innerHTML = `${icon("play")}<span>${audio.currentTime > 0 && !audio.ended ? "Fortsätt lyssna" : "Lyssna"}</span>`; narrator.classList.remove("is-playing"); });
    audio.addEventListener("ended", () => {
      button.innerHTML = `${icon("play")}<span>Lyssna igen</span>`;
      narrator.classList.remove("is-playing");
      if (audio.isConnected) this.audioPositions.delete(this.audioPositionKey(segment.sceneId));
      if (!this.progress.listenedSceneIds.includes(segment.sceneId)) this.progress.listenedSceneIds.push(segment.sceneId);
      this.persist();
    });
    audio.addEventListener("error", () => { caption.textContent = "Ljudfilen saknas ännu. Hela manuset finns under Läs som text."; });
    const savedPosition = this.audioPositions.get(this.audioPositionKey(segment.sceneId)) ?? 0;
    if (savedPosition > 0) {
      const restore = (): void => {
        if (Number.isFinite(audio.duration) && savedPosition < audio.duration - 0.15) audio.currentTime = savedPosition;
        button.innerHTML = `${icon("play")}<span>Fortsätt lyssna</span>`;
        update();
      };
      if (audio.readyState >= 1) restore();
      else audio.addEventListener("loadedmetadata", restore, { once: true });
    }
  }

  private captureAudioPosition(): void {
    const narrator = this.root.querySelector<HTMLElement>("[data-k01-narrator]");
    const audio = narrator?.querySelector<HTMLAudioElement>("[data-k01-audio]");
    const sceneId = narrator?.dataset.sceneId;
    if (audio && sceneId && audio.currentTime > 0 && !audio.ended) this.audioPositions.set(this.audioPositionKey(sceneId), audio.currentTime);
  }

  private updateContinueButton(): void {
    const button = this.root.querySelector<HTMLButtonElement>("[data-k01-action='next-scene']");
    if (!button) return;
    const ready = isSceneReady(this.currentScene(), this.currentSceneProgress());
    button.disabled = !ready;
    const gate = this.root.querySelector<HTMLElement>("[data-k01-gate-message]");
    if (gate) gate.textContent = ready ? "Klart – du kan fortsätta" : "Prova klart först";
  }

  private updateArtifactControls(): void {
    const artifact = k01ArtifactById.get(this.currentScene().interaction.artifactId);
    if (!artifact) return;
    const progress = this.currentSceneProgress();
    const completion = getArtifactCompletion(artifact, progress);
    const count = this.root.querySelector<HTMLElement>("[data-k01-artifact-count]");
    if (count) count.textContent = `${completion.completed}/${completion.total} klara`;
    const page = Math.max(0, Math.min(artifact.fields.length - 1, progress.numbers.artifactPage ?? 0));
    const currentReady = isArtifactFieldComplete(artifact.fields[page], progress);
    const next = this.root.querySelector<HTMLButtonElement>("[data-k01-artifact-next]");
    if (next) next.disabled = !currentReady || page === artifact.fields.length - 1;
    const stepStatus = this.root.querySelector<HTMLElement>("[data-k01-artifact-step-status]");
    if (stepStatus) stepStatus.textContent = currentReady ? "Klart" : "Svara kort för att gå vidare";
    const button = this.root.querySelector<HTMLButtonElement>("[data-k01-action='save-artifact']");
    if (button) button.disabled = !completion.ready;
  }

  private focusSceneTitle(): void {
    window.requestAnimationFrame(() => this.root.querySelector<HTMLElement>("[data-k01-scene-title]")?.focus({ preventScroll: true }));
  }

  private scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: this.motionReduced ? "auto" : "smooth" });
  }

  private async loadMediaManifest(): Promise<void> {
    try {
      const response = await fetch(K01_MEDIA_MANIFEST);
      if (!response.ok) return;
      const manifest = await response.json() as { files?: unknown };
      if (!Array.isArray(manifest.files)) return;
      for (const candidate of manifest.files) {
        if (!candidate || typeof candidate !== "object") continue;
        const item = candidate as { sceneId?: unknown; file?: unknown; cues?: unknown; playable?: unknown };
        if (typeof item.sceneId !== "string" || typeof item.file !== "string" || !Array.isArray(item.cues)) continue;
        if (item.playable === false) continue;
        const cues = item.cues.flatMap((cue): K01TimedCue[] => {
          if (!cue || typeof cue !== "object") return [];
          const source = cue as { startSeconds?: unknown; endSeconds?: unknown; text?: unknown };
          return typeof source.startSeconds === "number" && typeof source.endSeconds === "number" && typeof source.text === "string"
            ? [{ startSeconds: source.startSeconds, endSeconds: source.endSeconds, text: source.text.slice(0, 500) }]
            : [];
        });
        this.mediaByScene.set(item.sceneId, { sceneId: item.sceneId, file: item.file, cues });
      }
    } catch {
      // Full text and the short built-in caption cues remain available offline.
    }
  }

  private restoreFocus(key?: string, value?: string): void {
    if (!key) return;
    const valueSelector = value === undefined ? "" : `[data-value="${CSS.escape(value)}"]`;
    window.requestAnimationFrame(() => this.root.querySelector<HTMLElement>(`[data-key="${CSS.escape(key)}"]${valueSelector}`)?.focus());
  }

  private focusConsequence(): void {
    window.requestAnimationFrame(() => this.root.querySelector<HTMLElement>(".k01-consequence, .k01-coach")?.scrollIntoView({ block: "nearest", behavior: this.motionReduced ? "auto" : "smooth" }));
  }
}
